import { useConfirm } from '@euler/functions/ux/useConfirm';
import {
  BiDashboardOp,
  BiDashboardOpKind,
  BiLayoutItem,
  BiLayoutMode,
} from '@euler/model/bi/dashboard';
import { useCallback } from 'react';
import { biCustomToPreset } from '../../functions/biGridLayout';
import { BiApplyOutcome, BiDashboardState } from './useBiDashboard';

/**
 * Layout writes: a drop stores the whole grid as a custom layout (the
 * first one converts a preset), a phone resize stores one card's phone
 * height, and Reset resets the layout the viewer is looking at only — on a
 * phone it clears the phone heights, the wide grid untouched; on a wide
 * screen it returns the grid to a preset in the current reading order, the
 * phone heights kept. Each goes through `set_layout` on the revision on
 * screen, so a stale grid is refused whole; the server keeps the phone
 * heights through a layout that does not carry them.
 */
export function useBiLayoutEditing(
  state: BiDashboardState,
  apply: (
    ops: BiDashboardOp[],
    text: string,
    baseRevision?: number,
  ) => Promise<BiApplyOutcome>,
  /** Whether the phone view is drawn; null before the grid measured. */
  stacked: boolean | null,
) {
  const layout = state.kind === 'ready' ? state.dashboard.layout : null;
  const confirm = useConfirm();
  const arrange = useCallback(
    async (items: BiLayoutItem[]) =>
      (
        await apply(
          [
            {
              op: BiDashboardOpKind.SetLayout,
              layout: { mode: BiLayoutMode.Custom, items },
            },
          ],
          layout?.mode === BiLayoutMode.Preset
            ? '已改为自定义布局'
            : '已调整布局',
        )
      ).ok,
    [apply, layout?.mode],
  );
  // The phone height of one card, the wide layout untouched.
  const resizeNarrow = useCallback(
    async (cardId: string, rows: number) => {
      if (!layout) return false;
      return (
        await apply(
          [
            {
              op: BiDashboardOpKind.SetLayout,
              layout: {
                ...layout,
                narrowRows: { ...layout.narrowRows, [cardId]: rows },
              },
            },
          ],
          '已调整高度',
        )
      ).ok;
    },
    [apply, layout],
  );
  const narrowed = !!layout && Object.keys(layout.narrowRows ?? {}).length > 0;
  const resetNarrow = useCallback(async () => {
    if (!layout) return;
    const ok = await confirm({
      title: '重置卡片高度',
      message: '手机上调整过的卡片高度将恢复默认，电脑上的布局不受影响。',
      confirmButtonText: '重置',
      destructive: true,
    });
    if (!ok) return;
    await apply(
      [
        {
          op: BiDashboardOpKind.SetLayout,
          layout: { ...layout, narrowRows: {} },
        },
      ],
      '已重置手机上的卡片高度',
    );
  }, [apply, confirm, layout]);
  const resetWide = useCallback(async () => {
    if (layout?.mode !== BiLayoutMode.Custom) return;
    const ok = await confirm({
      title: '重置布局',
      message:
        '卡片将按当前顺序自动排列，大小恢复默认；手机上调整过的卡片高度不受影响。',
      confirmButtonText: '重置',
      destructive: true,
    });
    if (!ok) return;
    // A preset without `narrowRows`: the stored phone heights stay.
    await apply(
      [{ op: BiDashboardOpKind.SetLayout, layout: biCustomToPreset(layout) }],
      '已重置为自动布局',
    );
  }, [apply, confirm, layout]);
  return {
    arrange,
    resizeNarrow,
    // Offered while the layout on screen has something to reset: a card
    // resized on a phone, a custom grid on a wide screen.
    reset:
      stacked === null
        ? null
        : stacked
        ? narrowed
          ? resetNarrow
          : null
        : layout?.mode === BiLayoutMode.Custom
        ? resetWide
        : null,
  };
}
